import { v4 as uuid } from 'uuid';

import type { Effects, ReduxSagaModel } from './types';
import type { BedrockMessage, BedrockMessageContent } from '@/utils/beemAgentTypes';
import Dataset from '@/services/dataset';
import Folder from '@/services/folder';
import { conversationFolderName } from '@/services/conversationFolderName';
import DatasetTest from '@/services/datasetTest';
import GenerativeDashboard from '@/services/generativeDashboard';
import InsightTurn from '@/services/insightTurn';
import InsightFeedback from '@/services/insightFeedback';
import type { FeedbackContext, FeedbackReason } from '@/services/insightFeedback';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import { assertIsDefined } from '@/utils/typeChecks';
import { handleSystemError, handleUnknownError, handleUserError } from '@/utils/handleError';
import { renderContextBlock, isContextMeaningful } from '@/utils/organizationAIContext';
import {
  collectScreenContext,
  dataObjectOnScreen,
  renderScreenContextBlock,
} from '@/utils/screenContext';
import { renderProjectContextBlock } from '@/utils/projectContext';
import { projectsRepo } from '@/pages/Insights/data/projectsRepo';
import { conversationsRepo } from '@/pages/Insights/data/conversationsRepo';
import { addOrgWsPrefix } from '@/utils';
import { message } from 'antd';
import { openDatasetEditorWithSql } from '@/components/Chat/StepActionButtons';
import { trackButtonClick } from '@/utils/trackEvent';
import { getDvaApp } from 'umi';
import { finishInBackground } from './insightBackgroundTurn';
import { errorReply, stoppedReply } from './insightReplies';

const ns = 'app.models.insight';

function isValidAssistantMessage(message: BedrockMessage): boolean {
  return (
    message.role === 'assistant' &&
    !message.content.some((item) => item.toolUse) &&
    !message.content.some((item) => item.toolResult)
  );
}

export type State = {
  queryLoading: boolean;
  chatHistory: BedrockMessage[];
  generatedDescription: string;
  // Keys the agent's long-term memory for the open conversation. Minted on the conversation's
  // first turn, restored on resume, and cleared with the thread, so a new conversation gets a
  // new id. Undefined until then.
  sessionId?: string;
  // The saved conversation record (conversationsRepo) the open thread maps to. Held here, next to
  // the thread itself, because the Insights page and the Ask AI panel show the same thread: kept
  // per surface, the panel saw a thread with no record of its own and saved a second copy.
  conversationId?: string;
  // Dashboards the agent started building in this thread, by id, as last checked: the card under
  // the answer follows them until they are ready.
  dashboardStatuses: Record<string, { status: string; progressStep: string | null }>;
  // The proposed test whose "Create the test" card is being created, by its card's key.
  creatingTest: string | null;
  // Datasets being created from the chat's "Create the dataset" card, by the card's key: created,
  // deployed, then its first run. Held only while the app is open; the card offers to create it
  // again after a reload, and a second create fails on the view name the first one took.
  datasetBuilds: Record<string, DatasetBuild>;
  // A conversation to reopen once the chat mounts: the one the user left for the add-source flow
  // (src/utils/chatReturn.ts). ChatUI resumes it, sends `followUp` in it so the agent carries on,
  // and clears this.
  // Without an id, the chat starts a new conversation instead, and sends `followUp` as its first
  // question.
  // `seed` starts the new conversation from messages already exchanged (a dashboard AI Insights
  // built, whose chat opens on the turn that built it), with that turn's memory session.
  pendingResume?: {
    id?: string;
    followUp?: string;
    seed?: { messages: BedrockMessage[]; sessionId?: string };
  };
  // The question being answered, while the agent works on it, and what was asked. The turn runs
  // in the background (BEEM-1448); ChatPage follows it and feeds each snapshot to receiveTurn.
  currentTurn: InsightTurn | null;
  currentRequest: StartQueryEffectPayload | null;
  // The last question that failed, kept so it can be retried as it was asked.
  failedRequest: StartQueryEffectPayload | null;
  // What the conversation history does not keep about an answer, keyed by the index of the
  // answer's message in chatHistory: the agent's reasoning summary and whether it used memories.
  traces: InsightTraces;
  // The answer whose thumbs-down is being sent, by its index in chatHistory.
  reportingFeedback: number | null;
};

// A thumbs-down, once it reached the BEEM team. `ticketId` is the HubSpot ticket, null where the
// environment files none.
export type AnswerFeedback = { reason: FeedbackReason; at: string; ticketId: string | null };

export type InsightTrace = {
  reasoningText?: string;
  memoryCount?: number;
  feedback?: AnswerFeedback;
};
export type InsightTraces = Record<number, InsightTrace>;

const initialState: State = {
  queryLoading: false,
  chatHistory: [],
  generatedDescription: '',
  sessionId: undefined,
  dashboardStatuses: {},
  creatingTest: null,
  datasetBuilds: {},
  currentTurn: null,
  currentRequest: null,
  failedRequest: null,
  traces: {},
  reportingFeedback: null,
};

type QueryLoadingAction = {
  type: 'queryLoading';
  payload: Pick<State, 'queryLoading'>;
};

type SaveChatHistoryAction = {
  type: 'saveChatHistory';
  payload: Pick<State, 'chatHistory'>;
};

type SaveSessionIdAction = {
  type: 'saveSessionId';
  payload: Pick<State, 'sessionId'>;
};

export type CheckDashboardStatusEffect = {
  type: 'insight/checkDashboardStatus';
  payload: { id: string };
};

// The chat's "Create the test" card: a draft test on the dataset, the same row the Create Dataset
// Test dialog writes, then the test editor opened on it with the proposed SQL in it, unsaved.
export type CreateProposedTestEffect = {
  type: 'insight/createProposedTest';
  payload: { key: string; datasetId: string; name: string; sql: string };
};

export type DatasetBuild = {
  stage: 'creating' | 'deploying' | 'running' | 'ready' | 'failed' | 'draft';
  datasetId?: string;
  error?: string;
  // What the first refresh produced, once `ready` and the dataset's stats have caught up.
  rowCount?: number;
  columnCount?: number;
};

// After its first refresh, a dataset's stats still hold the deploy's placeholder (no columns) until
// the pipeline records the run, a moment later. The card waits this long for them, at most.
const FIRST_RUN_STATS_TRIES = 5;
const FIRST_RUN_STATS_INTERVAL_MS = 2000;

// The chat's "Create the dataset" card: the dataset the agent proposed, created in the
// workspace's root folder and deployed, as the editor's Deploy does, then followed to the end of
// its first run so the card can say how it went.
export type CreateProposedDatasetEffect = {
  type: 'insight/createProposedDataset';
  // `review`: create it as a draft and open it in the editor with the SQL, instead of deploying.
  payload: { key: string; name: string; sql: string; review?: boolean };
};

// A first run that takes longer than this is left to the dataset's own page.
const FIRST_RUN_WAIT_MS = 15 * 60 * 1000;

export type SaveConversationIdAction = {
  type: 'insight/saveConversationId';
  payload: Pick<State, 'conversationId'>;
};

type SaveTurnAction = {
  type: 'saveTurn';
  payload: Partial<Pick<State, 'currentTurn' | 'currentRequest' | 'failedRequest'>>;
};

type SaveCreatingTestAction = {
  type: 'saveCreatingTest';
  payload: Pick<State, 'creatingTest'>;
};

type SaveDatasetBuildAction = {
  type: 'saveDatasetBuild';
  payload: { key: string; build: DatasetBuild };
};

export type SavePendingResumeAction = {
  type: 'insight/savePendingResume';
  payload: Pick<State, 'pendingResume'>;
};

type SaveTracesAction = {
  type: 'saveTraces';
  payload: Pick<State, 'traces'>;
};

type SaveReportingFeedbackAction = {
  type: 'saveReportingFeedback';
  payload: Pick<State, 'reportingFeedback'>;
};

// A thumbs-down on the answer at `messageIndex` in chatHistory.
export type ReportFeedbackEffect = {
  type: 'insight/reportFeedback';
  payload: { messageIndex: number; reason: FeedbackReason; note: string };
};

type SaveGeneratedDescriptionAction = {
  type: 'saveGeneratedDescription';
  payload: Pick<State, 'generatedDescription'>;
};

export type StartQueryEffectPayload = {
  prompt: string;
  // Dataset ids (Dataset.id) the user scoped the query to. Undefined/empty means
  // "all available datasets" (the default when no explicit selection is made).
  datasetIds?: string[];
  // When true (the summonable AI panel), the query can use EVERY dataset in the current workspace,
  // not just the AI-searchable ones. Purely a frontend scoping decision — the agent still queries
  // as the workspace's own Redshift user, which already has read access to those views.
  allWorkspaceDatasets?: boolean;
  // The AI Insights project the conversation belongs to; its instructions and knowledge are sent
  // with every question.
  projectId?: string;
  // A dashboard turn (the agent's dashboard profile): the question builds or edits this
  // dashboard, through its page tools, focused on the cards the user selected.
  dashboard?: {
    id: string;
    focusCards?: string[];
    // Files attached to the question, already uploaded (useAttachmentUpload).
    attachments?: Array<{ s3Key: string; contentType: string; size: number; filename: string }>;
  };
  // Asking a failed question again: the question and its error reply are the last two messages,
  // and are left out of what the agent sees (and replaced) rather than removed up front, so the
  // conversation never goes empty (which would start a new record) while the retry runs.
  retry?: boolean;
};

export type ReceiveTurnEffect = {
  type: 'insight/receiveTurn';
  payload: { turn: InsightTurn };
};

export type GetTurnEffect = {
  type: 'insight/getTurn';
  payload: { id: string };
};

export type StopTurnEffect = {
  type: 'insight/stopTurn';
  payload: Record<string, never>;
};

export type AbandonTurnEffect = {
  type: 'insight/abandonTurn';
  payload: Record<string, never>;
};

export type GenerateDescriptionEffectPayload = {
  context: string;
  description: string;
};

const textOf = (message: BedrockMessage) =>
  message.content
    .map((item) => item.text)
    .filter(Boolean)
    .join('\n');

// What a thumbs-down says about the answer at `index`: the question it answered, the tools it
// ran, and sizes. Never the answer or what the tools returned, which hold the customer's data.
export const feedbackContext = (
  history: BedrockMessage[],
  index: number,
  trace: InsightTrace | undefined,
  conversationId: string | undefined,
  page: string,
): FeedbackContext => {
  let start = index - 1;
  // The question is the closest user message with text; tool results are user messages too.
  while (start >= 0 && !(history[start].role === 'user' && textOf(history[start]))) start -= 1;
  const tools = history
    .slice(start + 1, index)
    .flatMap((m) => m.content)
    .map((item) => item.toolUse?.name)
    .filter((name): name is string => !!name);
  return {
    question: start >= 0 ? textOf(history[start]) : undefined,
    conversationId,
    messageIndex: index,
    tools,
    answerLength: history[index] ? textOf(history[index]).length : 0,
    memoryCount: trace?.memoryCount,
    hadReasoning: !!trace?.reasoningText,
    page,
  };
};

// Stands in for a message the agent returned with nothing in it (BEEM-2324).
const EMPTY_MESSAGE_TEXT = '(no reply)';

// A text block Bedrock refuses: an empty or blank `text`, with nothing else in the block.
const isBlankText = (block: BedrockMessageContent) =>
  Object.keys(block).length === 1 && typeof block.text === 'string' && !block.text.trim();

// Only what the agent's contract defines. The chat view annotates messages in place (see
// appendMetadata in ChatConversations), and history must go back to the agent unmodified.
// The one exception is content Bedrock rejects. The model can end a turn without saying anything,
// and the agent stores that as an assistant message with no content. Replayed, it fails every
// later question with "The content field in the Message object at messages.N is empty". It gets a
// placeholder rather than being dropped, because dropping it would leave two user messages in a
// row, which Bedrock rejects too.
const forAgent = (messages: BedrockMessage[]): BedrockMessage[] =>
  messages
    .filter((el) => el.role === 'user' || el.role === 'assistant')
    .map(({ role, content }) => {
      const kept = (content ?? []).filter((block) => !isBlankText(block));
      return { role, content: kept.length ? kept : [{ text: EMPTY_MESSAGE_TEXT }] };
    });

// A turn whose conversation the user left runs on, and lands in that conversation
// (insightBackgroundTurn). When that conversation is the one open again by then, and nothing is
// being asked in it, the view takes the saved thread too.
const followInBackground = (turnId: string, conversationId: string) => {
  finishInBackground(turnId, conversationId, (saved) => {
    const store = getDvaApp()._store;
    const { insight } = store.getState() as { insight: State };
    if (insight.conversationId !== conversationId || insight.currentRequest) return;
    store.dispatch({ type: 'insight/saveTraces', payload: { traces: saved.traces ?? {} } });
    store.dispatch({ type: 'insight/saveChatHistory', payload: { chatHistory: saved.messages } });
  })
    .then((outcome) =>
      logger.debug({ label: `${ns}.background`, message: { turnId, conversationId, outcome } }),
    )
    .catch((e) => handleSystemError(e, `${ns}.background`));
};

// Questions left while their turn was still being started, with the conversation they were
// asked in: invokeConverse hands the turn to followInBackground once it has an id.
const leftWhileStarting = new WeakMap<StartQueryEffectPayload, string | undefined>();

// Records a failed question the way the conversation shows it: the question, then an error
// reply. The question is kept so it can be retried.
function* failTurn(put: Effects['put'], request: StartQueryEffectPayload, detail: string) {
  const stored = yield* sgselect((s) => s.insight.chatHistory);
  // A retry replaces the failure it retries instead of stacking a second one under it.
  const chatHistory = request.retry ? stored.slice(0, -2) : stored;
  yield put<SaveChatHistoryAction>({
    type: 'saveChatHistory',
    payload: {
      chatHistory: [
        ...chatHistory,
        { role: 'user', content: [{ text: request.prompt }] },
        errorReply(detail),
      ],
    },
  });
  yield put<SaveTurnAction>({
    type: 'saveTurn',
    payload: { currentTurn: null, currentRequest: null, failedRequest: request },
  });
  yield put<QueryLoadingAction>({ type: 'queryLoading', payload: { queryLoading: false } });
}

// Folds one snapshot of the running turn into the state, and finishes the turn when the snapshot
// is terminal. Snapshots come from both the subscription and the poll, so the same one can arrive
// twice: anything for another turn, or after the terminal one, is ignored.
function* receive(put: Effects['put'], incoming: InsightTurn) {
  const current = yield* sgselect((s) => s.insight.currentTurn);
  const request = yield* sgselect((s) => s.insight.currentRequest);
  if (!current || current.id !== incoming.id || current.isTerminal || !request) return;

  const turn = incoming.isStale
    ? incoming.withStatus('ERROR', 'The assistant stopped responding. Please try again.')
    : incoming;
  // A terminal snapshot is stored before anything is awaited, so a duplicate stops at the check above.
  yield put<SaveTurnAction>({ type: 'saveTurn', payload: { currentTurn: turn } });
  if (!turn.isTerminal) return;

  if (turn.status === 'CANCELLED') {
    // A stopped question stays in the conversation with what had been written of its answer,
    // marked as stopped (QA 2330: Stop used to remove the question and the partial answer). A
    // stopped retry leaves the conversation as it was, with the earlier failure still showing.
    const chatHistory = yield* sgselect((s) => s.insight.chatHistory);
    yield put<SaveChatHistoryAction>({
      type: 'saveChatHistory',
      payload: {
        chatHistory: request.retry
          ? [...chatHistory]
          : [
              ...chatHistory,
              { role: 'user', content: [{ text: request.prompt }] },
              stoppedReply(turn.answerText),
            ],
      },
    });
    yield put<SaveTurnAction>({
      type: 'saveTurn',
      payload: {
        currentTurn: null,
        currentRequest: null,
        // A stopped retry leaves the earlier failure showing, so it stays retryable.
        ...(request.retry ? { failedRequest: request } : {}),
      },
    });
    yield put<QueryLoadingAction>({ type: 'queryLoading', payload: { queryLoading: false } });
    return;
  }

  if (turn.status === 'ERROR') {
    yield* failTurn(put, request, turn.error || 'Unknown error');
    return;
  }

  try {
    const messages = yield* sgcall(() => InsightTurn.getMessages(turn.id));
    const lastRow = messages[messages.length - 1];
    if (!lastRow || !isValidAssistantMessage(lastRow))
      throw new Error(`Received invalid message: ${JSON.stringify(lastRow)}`);
    const traces = yield* sgselect((s) => s.insight.traces);
    // Traces first: ChatUI persists the conversation as soon as the history changes, and reads
    // the traces at that moment.
    if (turn.reasoningText || turn.memoryCount > 0)
      yield put<SaveTracesAction>({
        type: 'saveTraces',
        payload: {
          traces: {
            ...traces,
            [messages.length - 1]: {
              reasoningText: turn.reasoningText,
              memoryCount: turn.memoryCount,
            },
          },
        },
      });
    // The agent's messages are the source of truth for the history.
    yield put<SaveChatHistoryAction>({
      type: 'saveChatHistory',
      payload: { chatHistory: messages },
    });
    yield put<SaveTurnAction>({
      type: 'saveTurn',
      payload: { currentTurn: null, currentRequest: null },
    });
    yield put<QueryLoadingAction>({ type: 'queryLoading', payload: { queryLoading: false } });
  } catch (e) {
    handleSystemError(e, `${ns}.receiveTurn`);
    yield* failTurn(put, request, e instanceof Error ? e.message : 'Unknown error');
  }
}

const InsightModel: ReduxSagaModel<
  State,
  {
    queryLoading: QueryLoadingAction;
    saveChatHistory: SaveChatHistoryAction;
    saveSessionId: SaveSessionIdAction;
    saveConversationId: { type: 'saveConversationId'; payload: Pick<State, 'conversationId'> };
    saveDashboardStatus: {
      type: 'saveDashboardStatus';
      payload: { id: string; status: string; progressStep: string | null };
    };
    saveTurn: SaveTurnAction;
    saveTraces: SaveTracesAction;
    saveCreatingTest: SaveCreatingTestAction;
    saveDatasetBuild: SaveDatasetBuildAction;
    savePendingResume: Omit<SavePendingResumeAction, 'type'> & { type: 'savePendingResume' };
    saveGeneratedDescription: SaveGeneratedDescriptionAction;
    saveReportingFeedback: SaveReportingFeedbackAction;
  }
> = {
  namespace: 'insight',
  state: initialState,
  effects: {
    *invokeMockQuery(_, { call, put }) {
      const responses = [
        'What specific assistance do you require?',
        'Could you please provide more details?',
        'How can I support you right now?',
        "I'm ready to assist. What's on your mind?",
        'Let me know how I can be of service to you right now.',
        "How can I support you today? Let's address your needs.",
        'Tell me more about your requirements so I can assist you better.',
        "I'm here to help. Please specify what kind of assistance you need so I can assist you effectively.",
        "Feel free to ask me anything! I'm here to provide you with the support you need.",
        'Let me know how I can be of service to you right now. Your satisfaction is my priority.',
      ];
      const randomIndex = Math.floor(Math.random() * responses.length);

      yield put<QueryLoadingAction>({
        type: 'queryLoading',
        payload: {
          queryLoading: true,
        },
      });

      const messages = yield* sgselect((s) => s.insight.chatHistory);
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      yield call(delay, 1000);

      yield put<SaveChatHistoryAction>({
        type: 'saveChatHistory',
        payload: {
          chatHistory: [
            ...messages,
            {
              role: 'assistant',
              content: [
                {
                  text: responses[randomIndex],
                },
              ],
            },
          ],
        },
      });

      yield put<QueryLoadingAction>({
        type: 'queryLoading',
        payload: {
          queryLoading: false,
        },
      });
    },
    *invokeConverse({ payload }: { payload: StartQueryEffectPayload }, { put }) {
      const { prompt, datasetIds, allWorkspaceDatasets } = payload;
      yield put<QueryLoadingAction>({
        type: 'queryLoading',
        payload: {
          queryLoading: true,
        },
      });
      yield put<SaveTurnAction>({
        type: 'saveTurn',
        payload: { currentTurn: null, currentRequest: payload, failedRequest: null },
      });

      const conversationId = yield* sgselect((st) => st.insight.conversationId);
      const currentWorkspace = yield* sgselect(
        (s) => s.organization.currentWorkspace,
        'BEEM240711183312',
      );
      const storedHistory = yield* sgselect((s) => s.insight.chatHistory);
      const chatHistory = forAgent(payload.retry ? storedHistory.slice(0, -2) : storedHistory);
      let sessionId = yield* sgselect((s) => s.insight.sessionId);
      if (!sessionId) {
        sessionId = uuid();
        yield put<SaveSessionIdAction>({ type: 'saveSessionId', payload: { sessionId } });
      }

      const aiCtx = yield* sgselect((s) => s.organizationAIContext.current);
      const logoUrl = yield* sgselect((s) => s.organizationAIContext.logoSignedUrl);
      const iconUrl = yield* sgselect((s) => s.organizationAIContext.iconSignedUrl);
      const logoDarkUrl = yield* sgselect((s) => s.organizationAIContext.logoDarkSignedUrl);
      const iconDarkUrl = yield* sgselect((s) => s.organizationAIContext.iconDarkSignedUrl);
      const orgForCtx = yield* sgselect((s) => s.organization.current);

      let organizationContext: string | undefined;
      const ctxSnapshot = {
        industry: aiCtx?.industry,
        description: aiCtx?.description,
        industries: aiCtx?.industries,
        business: aiCtx?.business,
        customers: aiCtx?.customers,
        mission: aiCtx?.mission,
        differentiators: aiCtx?.differentiators,
        objectives: aiCtx?.objectives,
        productsServices: aiCtx?.productsServices,
        companyType: aiCtx?.companyType,
        employeeCount: aiCtx?.employeeCount,
        foundedDate: aiCtx?.foundedDate,
        markets: aiCtx?.markets,
        website: aiCtx?.website,
        primaryColor: aiCtx?.primaryColor,
        secondaryColor: aiCtx?.secondaryColor,
        accentColor: aiCtx?.accentColor,
        backgroundColor: aiCtx?.backgroundColor,
        surfaceColor: aiCtx?.surfaceColor,
        borderColor: aiCtx?.borderColor,
        textColor: aiCtx?.textColor,
        mutedTextColor: aiCtx?.mutedTextColor,
        successColor: aiCtx?.successColor,
        warningColor: aiCtx?.warningColor,
        errorColor: aiCtx?.errorColor,
        additionalInstructions: aiCtx?.additionalInstructions,
        logoSignedUrl: logoUrl,
        iconSignedUrl: iconUrl,
        logoDarkSignedUrl: logoDarkUrl,
        iconDarkSignedUrl: iconDarkUrl,
      };
      if (orgForCtx && isContextMeaningful(ctxSnapshot)) {
        organizationContext = renderContextBlock(ctxSnapshot, {
          name: orgForCtx.name,
          color: orgForCtx.color,
        });
      }

      // Append a <screen_context> block describing the screen/entity the user is currently viewing,
      // so the agent can resolve references like "this dashboard". Piggybacks on the existing
      // organizationContext string field — no GraphQL schema change required.
      const fullState = yield* sgselect((s) => s, 'BEEM250727AISCREENCTX');
      const screenSnapshot = collectScreenContext(fullState, window.location.pathname);
      const onScreenDataObject = dataObjectOnScreen(fullState, window.location.pathname);
      if (screenSnapshot) {
        const screenBlock = renderScreenContextBlock(screenSnapshot);
        organizationContext = organizationContext
          ? `${organizationContext}\n${screenBlock}`
          : screenBlock;
      }
      if (payload.projectId) {
        const project = yield* sgcall(() => projectsRepo.get(payload.projectId as string));
        const projectBlock = project ? renderProjectContextBlock(project) : undefined;
        if (projectBlock)
          organizationContext = organizationContext
            ? `${organizationContext}\n${projectBlock}`
            : projectBlock;
      }
      try {
        const id = yield* sgcall(async () => {
          // The pool of datasets the query may run against, all in the CURRENT workspace (owned +
          // shared-into-workspace) — never cross-workspace. In panel mode (`allWorkspaceDatasets`)
          // we include every dataset regardless of `enableSearchByAI`; otherwise only the curated
          // searchable ones. No grant change is needed: the agent queries as the workspace's own
          // Redshift user, which already has SELECT on these views.
          const ownedDatasets = await Dataset.getDatasetsOwnByWorkspace(
            currentWorkspace,
            undefined,
            allWorkspaceDatasets ? undefined : { enableSearchByAI: { eq: true } },
          );
          // Panel (allWorkspaceDatasets): every dataset ACCESSIBLE TO THE WORKSPACE USER — its own
          // datasets plus the ones shared into this workspace (the agent queries as this workspace's
          // Redshift user, which has SELECT on both). The standalone page keeps searchable-only.
          let sharedDatasets = await Dataset.getDatasetsSharedWithWorkspace(currentWorkspace);
          if (!allWorkspaceDatasets) {
            sharedDatasets = sharedDatasets.filter((el) => el.enableSearchByAI);
          }
          const pool = ownedDatasets.concat(sharedDatasets);
          // Scope to the user's selection when provided; fall back to the full pool if the
          // selection is empty or filters everything out (e.g. stale ids), so we never send an
          // empty dataset set to the backend.
          const scoped =
            datasetIds && datasetIds.length > 0
              ? pool.filter((d) => datasetIds.includes(d.id))
              : pool;
          const effective = scoped.length > 0 ? scoped : pool;
          const datasetAttrs = effective.map((dataset) => {
            const datasetId = dataset.dbViewsQualifiedName;
            assertIsDefined(datasetId, 'BEEM240712085850');
            return {
              id: datasetId,
              // Read by the CreateDashboard tool (provenance labels), ignored by the agent.
              name: dataset.name,
              // A dashboard's embed token covers only the workspace's AI-searchable datasets
              // (see embedDatasets), so CreateDashboard builds only from these.
              embeddable: !!dataset.enableSearchByAI,
              description: `${dataset.description}\n${dataset.createTableStatement}`,
            };
          });
          // The Datalake table on screen is queryable too: it is not a dataset, so it would
          // otherwise be missing, and "summarize this data" would have nothing to read.
          if (onScreenDataObject)
            datasetAttrs.push({
              id: onScreenDataObject.dbViewsQualifiedName,
              name: onScreenDataObject.name,
              embeddable: false,
              description: `Datalake source table "${onScreenDataObject.name}" (raw ingested data, open on screen)\n${onScreenDataObject.createTableStatement}`,
            });
          if (datasetAttrs.length === 0) {
            // A dashboard needs no dataset: with none enabled it is built on sample data, labelled
            // as such, and the flag makes the agent build it that way and its page check hold it
            // to that (no dataset reads, "Sample data" shown). A question needs data to answer.
            if (!payload.dashboard)
              throw new Error(
                'No dataset is enabled for AI search yet. Turn on AI search for a dataset in its Documentation panel, then ask again.',
              );
            const dashboardId = payload.dashboard.id;
            await GenerativeDashboard.update({ id: dashboardId, sampleData: true });
          }

          // Returns as soon as the turn is written; the answer is streamed into the InsightTurn
          // row, which ChatPage follows (subscription + poll) and hands to receiveTurn.
          return InsightTurn.start({
            workspaceId: currentWorkspace,
            datasets: JSON.stringify(datasetAttrs),
            prompt,
            messages: JSON.stringify(chatHistory),
            sessionId,
            // Links the agent's tools return (a dashboard it builds) open inside the app.
            linkBase: addOrgWsPrefix(''),
            ...(payload.dashboard
              ? {
                  profile: 'dashboard',
                  dashboardId: payload.dashboard.id,
                  focusCards: payload.dashboard.focusCards ?? [],
                  ...(payload.dashboard.attachments?.length
                    ? { attachments: JSON.stringify(payload.dashboard.attachments) }
                    : {}),
                }
              : {}),
            ...(organizationContext ? { organizationContext } : {}),
            // What the turn creates (a dashboard) links back to this conversation.
            ...(conversationId ? { conversationId } : {}),
          });
        });
        logger.debug({ label: `${ns}.invokeConverse.turn`, message: id });
        // The user may have moved to another conversation while the turn was being started: the
        // turn still finishes, into the conversation it was asked in.
        if ((yield* sgselect((s) => s.insight.currentRequest)) !== payload) {
          const leftFrom = leftWhileStarting.get(payload);
          if (leftFrom) followInBackground(id, leftFrom);
          else yield* sgcall(() => InsightTurn.cancel(id).catch(() => undefined));
          return;
        }
        yield put<SaveTurnAction>({
          type: 'saveTurn',
          payload: { currentTurn: InsightTurn.started(id) },
        });
      } catch (e) {
        handleSystemError(e, `${ns}.invokeConverse`);
        // Only into the conversation the question was asked in.
        if ((yield* sgselect((s) => s.insight.currentRequest)) === payload)
          yield* failTurn(put, payload, e instanceof Error ? e.message : 'Unknown error');
      }
    },
    *checkDashboardStatus(
      { payload: { id } }: { payload: CheckDashboardStatusEffect['payload'] },
      { put },
    ) {
      try {
        const { status, progressStep } = yield* sgcall(() => GenerativeDashboard.getStatus(id));
        yield put({ type: 'saveDashboardStatus', payload: { id, status, progressStep } });
      } catch (e) {
        // The card keeps its last known state and checks again on its next tick.
        logger.debug({ label: `${ns}.checkDashboardStatus.error`, message: e });
      }
    },
    *createProposedTest(
      { payload: { key, datasetId, name, sql } }: { payload: CreateProposedTestEffect['payload'] },
      { put },
    ) {
      yield put<SaveCreatingTestAction>({
        type: 'saveCreatingTest',
        payload: { creatingTest: key },
      });
      try {
        const workspaceId = yield* sgselect(
          (s) => s.organization.currentWorkspace,
          'BEEM261001101500',
        );
        const id = yield* sgcall(() => DatasetTest.create({ name, datasetId, workspaceId }));
        yield* sgcall(() =>
          openDatasetEditorWithSql({
            sql,
            editUrl: addOrgWsPrefix(`/hub/datasets/test/${id}/edit/draft`),
          }),
        );
      } catch (e) {
        message.error(handleUserError(e, `${ns}.createProposedTest.error`).formattedString);
      }
      yield put<SaveCreatingTestAction>({
        type: 'saveCreatingTest',
        payload: { creatingTest: null },
      });
    },
    *createProposedDataset(
      { payload: { key, name, sql, review } }: { payload: CreateProposedDatasetEffect['payload'] },
      { put },
    ) {
      const stage = (build: DatasetBuild) =>
        put<SaveDatasetBuildAction>({ type: 'saveDatasetBuild', payload: { key, build } });
      yield stage({ stage: 'creating' });
      let datasetId: string | undefined;
      try {
        const workspaceId = yield* sgselect(
          (s) => s.organization.currentWorkspace,
          'BEEM261001113000',
        );
        // Filed where the assistant's work is easy to find: AI Assistant > the user > the
        // conversation, and on for AI search, so the assistant and dashboards can read it.
        const user = yield* sgselect((s) => s.user.currentUser);
        const conversationId = yield* sgselect((s) => s.insight.conversationId);
        const conversation = conversationId
          ? yield* sgcall(() => conversationsRepo.get(conversationId))
          : undefined;
        // The conversation's folder is named once, by a short summary of its first question; later
        // datasets from the same conversation join the folder its earlier ones are in.
        const earlier = conversationId
          ? yield* sgcall(() =>
              Dataset.getDatasetsOwnByWorkspace(workspaceId, undefined, {
                sourceConversationId: { eq: conversationId },
              }),
            )
          : [];
        let folderId = earlier[0]?.folderId;
        if (!folderId) {
          const folderName = yield* sgcall(() =>
            conversationFolderName(conversation?.title ?? ''),
          );
          const root = yield* sgcall(() => Folder.getRootFolder(workspaceId));
          folderId = yield* sgcall(() =>
            Folder.ensurePath(root.id, [
              'AI Assistant',
              [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Me',
              folderName,
            ]),
          );
        }
        const id = yield* sgcall(() =>
          Dataset.create({
            name,
            folderId,
            workspaceId,
            enableSearchByAI: true,
            ...(conversationId ? { sourceConversationId: conversationId } : {}),
          }),
        );
        datasetId = id;
        if (review) {
          // Saved under its name, unsaved SQL in the editor: the user reviews and deploys it there.
          yield stage({ stage: 'draft', datasetId });
          yield* sgcall(() =>
            openDatasetEditorWithSql({
              sql,
              editUrl: addOrgWsPrefix(`/hub/datasets/${id}/edit/latest`),
            }),
          );
          return;
        }
        yield stage({ stage: 'deploying', datasetId });
        const dataset = yield* sgcall(() => Dataset.getDatasetById(id));
        // Listening starts before the deploy, so a quick first run is not missed.
        const firstRun = new Promise<'ready' | 'failed' | 'running'>((resolve) => {
          dataset.listenToPipelineResult(
            () => resolve('ready'),
            () => resolve('failed'),
          );
          setTimeout(() => resolve('running'), FIRST_RUN_WAIT_MS);
        });
        yield* sgcall(() =>
          dataset.deploy({
            sqlQuery: sql,
            trigger: dataset.trigger,
            triggerDetails: dataset.triggerDetails,
          }),
        );
        yield stage({ stage: 'running', datasetId });
        const outcome = yield* sgcall(() => firstRun);
        yield stage({ stage: outcome, datasetId });
        if (outcome === 'ready') {
          // The card says what the refresh produced. Best effort: without stats it keeps its
          // plain "Ready" line.
          const readStats = async (
            triesLeft: number,
          ): Promise<{ rowCount: number; columnCount: number } | null> => {
            const fresh = await Dataset.getDatasetById(id).catch(() => null);
            const columnCount = fresh?.columns?.length ?? 0;
            if (fresh && columnCount > 0 && typeof fresh.rowCount === 'number')
              return { rowCount: fresh.rowCount, columnCount };
            if (triesLeft <= 1) return null;
            await new Promise((r) => setTimeout(r, FIRST_RUN_STATS_INTERVAL_MS));
            return readStats(triesLeft - 1);
          };
          const stats = yield* sgcall(() => readStats(FIRST_RUN_STATS_TRIES));
          if (stats) yield stage({ stage: 'ready', datasetId, ...stats });
        }
      } catch (e) {
        const error = handleUserError(e, `${ns}.createProposedDataset.error`).formattedString;
        yield stage({ stage: 'failed', datasetId, error });
      }
    },
    *reportFeedback(
      { payload: { messageIndex, reason, note } }: { payload: ReportFeedbackEffect['payload'] },
      { put },
    ) {
      yield put<SaveReportingFeedbackAction>({
        type: 'saveReportingFeedback',
        payload: { reportingFeedback: messageIndex },
      });
      try {
        const workspaceId = yield* sgselect(
          (s) => s.organization.currentWorkspace,
          'BEEM261008120000',
        );
        const { chatHistory, traces, conversationId } = yield* sgselect((s) => s.insight);
        const context = feedbackContext(
          chatHistory,
          messageIndex,
          traces[messageIndex],
          conversationId,
          window.location.pathname,
        );
        const ticketId = yield* sgcall(() =>
          InsightFeedback.report({ workspaceId, reason, note: note.trim(), context }),
        );
        trackButtonClick('insights-answer-feedback-down', { reason });
        const feedback: AnswerFeedback = { reason, at: new Date().toISOString(), ticketId };
        const withFeedback = (all: InsightTraces): InsightTraces => ({
          ...all,
          [messageIndex]: { ...all[messageIndex], feedback },
        });
        // Recorded on the answer's trace, so the thumbs-down shows as sent wherever the
        // conversation is reopened, and cannot file a second ticket. The user may have moved to
        // another conversation meanwhile: the record goes on the one the answer is in.
        if ((yield* sgselect((s) => s.insight.conversationId)) === conversationId) {
          const latest = withFeedback(yield* sgselect((s) => s.insight.traces));
          yield put<SaveTracesAction>({ type: 'saveTraces', payload: { traces: latest } });
          if (conversationId) {
            const conversation = yield* sgcall(() => conversationsRepo.get(conversationId));
            if (conversation)
              yield* sgcall(() => conversationsRepo.upsert({ ...conversation, traces: latest }));
          }
        } else if (conversationId) {
          // Left before its first save landed, the thread has no id to find its record by: the
          // ticket is filed, and the thumbs-down shows as unsent there. A thread is saved as soon
          // as its first answer arrives, so this needs feedback sent within that moment.
          const conversation = yield* sgcall(() => conversationsRepo.get(conversationId));
          if (conversation)
            yield* sgcall(() =>
              conversationsRepo.upsert({
                ...conversation,
                traces: withFeedback(conversation.traces ?? {}),
              }),
            );
        }
      } catch (e) {
        message.error(handleUserError(e, `${ns}.reportFeedback.error`).formattedString);
      }
      yield put<SaveReportingFeedbackAction>({
        type: 'saveReportingFeedback',
        payload: { reportingFeedback: null },
      });
    },
    *receiveTurn({ payload: { turn } }: { payload: ReceiveTurnEffect['payload'] }, { put }) {
      yield* receive(put, turn);
    },
    // The poll behind the subscription: a snapshot missed while the socket reconnects, or one
    // that landed before the subscription was open, arrives this way.
    *getTurn({ payload: { id } }: { payload: GetTurnEffect['payload'] }, { put }) {
      try {
        const turn = yield* sgcall(() => InsightTurn.getById(id));
        yield* receive(put, turn);
      } catch (e) {
        // Transient: the next poll or snapshot tries again.
        logger.warn({ label: `${ns}.getTurn`, message: e });
      }
    },
    // Leaves the running turn behind because another conversation is being opened: the view lets
    // go of it at once, so its answer can never land in the conversation being opened. The turn
    // itself runs on and lands in the conversation it was asked in (followInBackground). Only a
    // turn whose conversation was never saved is stopped, since its answer would have nowhere to go.
    *abandonTurn(_, { put }) {
      const current = yield* sgselect((s) => s.insight.currentTurn);
      const request = yield* sgselect((s) => s.insight.currentRequest);
      const conversationId = yield* sgselect((s) => s.insight.conversationId);
      yield put<SaveTurnAction>({
        type: 'saveTurn',
        payload: { currentTurn: null, currentRequest: null },
      });
      yield put<QueryLoadingAction>({ type: 'queryLoading', payload: { queryLoading: false } });
      if (!current) {
        // Still being started: invokeConverse finds out it was left once the turn has an id.
        if (request) leftWhileStarting.set(request, conversationId);
        return;
      }
      if (current.isTerminal) return;
      if (conversationId) {
        followInBackground(current.id, conversationId);
        return;
      }
      try {
        yield* sgcall(() => InsightTurn.cancel(current.id));
      } catch (e) {
        handleSystemError(e, `${ns}.abandonTurn`);
      }
    },
    *stopTurn(_, { put }) {
      const current = yield* sgselect((s) => s.insight.currentTurn);
      if (!current || current.isTerminal) return;
      try {
        const status = yield* sgcall(() => InsightTurn.cancel(current.id));
        // Otherwise the turn finished first, and its last snapshot is on its way.
        if (status === 'CANCELLED') yield* receive(put, current.withStatus('CANCELLED'));
      } catch (e) {
        handleSystemError(e, `${ns}.stopTurn`);
      }
    },
    *generateDescription(
      { payload: { context, description } }: { payload: GenerateDescriptionEffectPayload },
      { put },
    ) {
      yield put<QueryLoadingAction>({
        type: 'queryLoading',
        payload: {
          queryLoading: true,
        },
      });

      try {
        const newDescription: string = yield* sgcall(async () => {
          const descriptionGenerationPrompt = `Write a short description or improve the existing one of the following dataset based on the metadata \
            provided. Your response should only contain the description without any chain of thought. Provide the \
            columns details in your answer. Avoid repeating the dataset name in the description. Never attempt to \
            query the table using tools.`;

          const { messages, metadata } = await graphql('beemAgentInsightConverse', {
            query: {
              datasets: JSON.stringify([]),
              prompt: `${descriptionGenerationPrompt} The following context is available: ${context}. The existing description: ${description}`,
              messages: JSON.stringify([]),
              // A one-shot rewrite: reasoning would only add latency.
              reasoning: false,
            },
          });

          logger.debug({
            label: `${ns}.generateDescription.messages`,
            message: JSON.stringify(messages, null, 2),
          });
          logger.debug({
            label: `${ns}.generateDescription.metadata`,
            message: JSON.stringify(metadata, null, 2),
          });

          return JSON.parse(messages)[1]?.content[0]?.text;
        });

        yield put<SaveGeneratedDescriptionAction>({
          type: 'saveGeneratedDescription',
          payload: {
            generatedDescription: newDescription,
          },
        });
      } catch (e) {
        handleUnknownError(e);
        yield put<SaveGeneratedDescriptionAction>({
          type: 'saveGeneratedDescription',
          payload: {
            generatedDescription: '',
          },
        });
      }
      yield put<QueryLoadingAction>({
        type: 'queryLoading',
        payload: {
          queryLoading: false,
        },
      });
    },
    *resetDescription(_, { put }) {
      yield put<SaveGeneratedDescriptionAction>({
        type: 'saveGeneratedDescription',
        payload: {
          generatedDescription: '',
        },
      });
    },
  },
  reducers: {
    resetAll() {
      // A new empty thread every time. ChatPage resyncs its copy of the thread only when this
      // array changes, and that copy can hold a question the store never received (shown while it
      // was being answered, then abandoned). Reusing initialState's array left that question on
      // screen, and a question asked from a project waited for an empty thread that never came.
      return { ...initialState, chatHistory: [] };
    },
    queryLoading(state, { payload }) {
      return { ...state, ...payload };
    },
    saveChatHistory(state, { payload }) {
      return { ...state, ...payload };
    },
    saveSessionId(state, { payload }) {
      return { ...state, ...payload };
    },
    saveConversationId(state, { payload }) {
      return { ...state, ...payload };
    },
    saveDashboardStatus(state, { payload: { id, status, progressStep } }) {
      return {
        ...state,
        dashboardStatuses: { ...state.dashboardStatuses, [id]: { status, progressStep } },
      };
    },
    saveCreatingTest(state, { payload }) {
      return { ...state, ...payload };
    },
    savePendingResume(state, { payload }) {
      return { ...state, ...payload };
    },
    saveDatasetBuild(state, { payload: { key, build } }) {
      return { ...state, datasetBuilds: { ...state.datasetBuilds, [key]: build } };
    },
    saveTurn(state, { payload }) {
      return { ...state, ...payload };
    },
    saveTraces(state, { payload }) {
      return { ...state, ...payload };
    },
    saveGeneratedDescription(state, { payload }) {
      return { ...state, ...payload };
    },
    saveReportingFeedback(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default InsightModel;
